import {useScreenTheme} from '@shared/hooks/useScreenTheme';
import React, {useMemo, useRef, useState} from 'react';
import {
  View,
  Text,
  Image,
  Dimensions,
  Platform,
  StatusBar,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  useWindowDimensions,
} from 'react-native';
import {createProfileStyles} from './Profile.styles';
import {ProfileProps} from './ProfileProps';
import {DropdownOption, TextTooltip} from '../ui';
import {profileOptions} from '@shared/constants/global';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {LogoutIcon} from '@shared/assets/icons/LogoutIcon';
import {useTranslation} from 'react-i18next';
import DotIcon from '@shared/assets/icons/DotIcon';
import { ClientThemeId } from '@shared/theme/clientThemes';
import { setClientTheme } from '@shared/store/themeSlice';
import {useIsZoomed} from '@shared/hooks/useSiteHeaderHeight';

// ---------------------------------------------------------------------------
// Module-level helpers for dropdown positioning — extracted from measureDropdown
// to remove the deeply-nested function's CC from ProfilePicture's total.
// ---------------------------------------------------------------------------

function computeDropdownTop(
  spaceBelow: number,
  spaceAbove: number,
  y: number,
  height: number,
  dropdownHeight: number,
  GAP: number,
  statusBarHeight: number,
): number {
  if (spaceBelow - 10 >= dropdownHeight + GAP) {
    return y + height + GAP;
  }
  if (spaceAbove >= dropdownHeight + GAP) {
    return spaceBelow >= dropdownHeight + GAP
      ? y - dropdownHeight - 15
      : y - dropdownHeight - 5;
  }
  if (spaceBelow >= spaceAbove) {
    return y + height + GAP;
  }
  return Math.max(statusBarHeight + GAP, y - dropdownHeight - GAP);
}

function computeDropdownLeft(
  x: number,
  windowWidth: number,
): {left: number | undefined; right: number | undefined} {
  if (x > windowWidth / 2) {
    return {left: undefined, right: 0};
  }
  return {left: x, right: undefined};
}

function measureAndSetDropdownPos(
  profileRef: React.RefObject<View>,
  setDropdownPos: (pos: {
    top: number;
    left: number | undefined;
    right: number | undefined;
    width: number;
  }) => void,
): void {
  if (!profileRef?.current) {return;}
  profileRef.current.measureInWindow(
    (x: number, y: number, width: number, height: number) => {
      const {width: windowWidth, height: windowHeight} = Dimensions.get('window');
      const statusBarHeight =
        Platform.OS === 'android' ? StatusBar.currentHeight || 0 : 0;
      const usableHeight = windowHeight - statusBarHeight;
      const ITEM_HEIGHT = 50;
      const GAP = 6;
      const MAX_DROPDOWN_HEIGHT = 200;
      const dropdownHeight = Math.min(
        profileOptions.length * ITEM_HEIGHT,
        MAX_DROPDOWN_HEIGHT,
      );
      const spaceBelow = usableHeight - (y + height);
      const spaceAbove = y - statusBarHeight;
      const top = computeDropdownTop(
        spaceBelow,
        spaceAbove,
        y,
        height,
        dropdownHeight,
        GAP,
        statusBarHeight,
      );
      const {left, right} = computeDropdownLeft(x, windowWidth);
      setDropdownPos({top, left, right, width});
    },
  );
}

/**
 * Handles keyboard activation (Enter/Space) for the profile trigger button.
 * Extracted to remove the nested arrow function's CC from ProfilePicture.
 */
function handleProfileTriggerKeyDown(e: any, onPress: () => void): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onPress();
  }
}

/**
 * Handles Escape key on the dropdown menu to close it.
 * Extracted to remove the nested arrow function's CC from ProfilePicture.
 */
function handleDropdownMenuKeyDown(e: any, onClose: () => void): void {
  if (e.key === 'Escape') {
    e.preventDefault();
    onClose();
  }
}

/**
 * Closes the profile dropdown and restores focus to the trigger (web).
 * Extracted to module level to remove the if/&& CC from ProfilePicture.
 */
function closeDropdown(
  setIsOpen: (v: boolean) => void,
  buttonRef: React.RefObject<any>,
): void {
  setIsOpen(false);
  if (Platform.OS === 'web' && buttonRef.current) {
    buttonRef.current.focus?.();
  }
}

/**
 * Re-measures dropdown position when orientation changes (only if open).
 * Extracted to module level to remove the if CC from ProfilePicture.
 */
function onOrientationChange(isOpen: boolean, measureDropdown: () => void): void {
  if (isOpen) {
    measureDropdown();
  }
}

// ---------------------------------------------------------------------------

interface ProfileSimpleViewProps {
  name: string | undefined;
  image: any;
  online: boolean;
  textColor: string | undefined;
  containerStyle: any;
  styles: any;
}

/**
 * Renders the non-interactive avatar used outside the header (no dropdown).
 * Extracted to allow ProfilePicture to use an early-return instead of a
 * top-level ternary, removing the nesting penalty from every inner branch.
 */
const ProfileSimpleView: React.FC<ProfileSimpleViewProps> = ({
  name, image, online, textColor, containerStyle, styles,
}) => (
  <View style={[styles.profileContainer, containerStyle]} testID="ProfileIdContainer">
    <View style={styles.avatar}>
      {image ? (
        <Image source={image} style={styles.imageContainer} resizeMode="cover" />
      ) : (
        <Text style={[styles.text, textColor ? {color: textColor} : undefined]}>
          {name}
        </Text>
      )}
    </View>
    {online && <View style={styles.onlineDot} />}
  </View>
);

interface ProfileDropdownMenuProps {
  dropdownPos: {top: number; left: number | undefined; right: number | undefined; width: number};
  image: any;
  name: string | undefined;
  fullName: string;
  displayFullName: string;
  maxNameChars: number;
  shiftStarted: boolean;
  selectedTechnician: any;
  isNameTooltipVisible: boolean;
  setIsNameTooltipVisible: (v: boolean) => void;
  handleClose: () => void;
  handleOrientationChange: () => void;
  logOut: () => void;
  handleSelect: (option: any) => void;
  styles: any;
  theme: any;
  t: (key: string) => string;
}

/** Available brand themes with their display swatch colours */
const THEME_SWATCHES: {id: ClientThemeId; color: string; label: string}[] = [
  {id: 'blue',   color: '#005FCC', label: 'Blue'},
  {id: 'teal',   color: '#0F766E', label: 'Teal'},
  {id: 'slate',  color: '#475569', label: 'Slate'},
  {id: 'orange', color: '#CC5500', label: 'Orange'},
  {id: 'purple', color: '#6A1B9A', label: 'Purple'},
];

/**
 * The Modal overlay + dropdown panel shown when the profile button is open.
 * Extracted to remove shiftStarted ternaries, Platform spreads, and image
 * ternaries from the ProfilePicture component CC.
 */
const ProfileDropdownMenu: React.FC<ProfileDropdownMenuProps> = ({
  dropdownPos, image, name, fullName, displayFullName, maxNameChars,
  shiftStarted, selectedTechnician, isNameTooltipVisible,
  setIsNameTooltipVisible, handleClose, handleOrientationChange,
  logOut, handleSelect, styles, theme, t,
}) => {
  const dispatch = useDispatch();
  const activeTheme = useSelector(
    (state: RootState) => state.theme?.themeName ?? 'blue',
  );
  // Extreme zoom (e.g. 320x256 CSS px at 400%): the anchored dropdown cannot fit
  // beside the trigger, so it becomes a full-width panel pinned to the top of the
  // viewport that scrolls if it is still too tall.
  const isZoomed = useIsZoomed();
  const zoomedMenuStyle: object | undefined = isZoomed
    ? {
        top: 8,
        left: 8,
        right: 8,
        minWidth: 0,
        maxHeight: 'calc(100vh - 16px)',
        overflowY: 'auto',
      }
    : undefined;

  return (
  <Modal
    testID="profileDropdownModal"
    accessibilityViewIsModal
    transparent
    visible
    animationType="none"
    supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}
    onOrientationChange={handleOrientationChange}>
    {/* Overlay */}
    <TouchableWithoutFeedback onPress={handleClose}>
      <View testID="profileOverlay" style={styles.modalOverlay} />
    </TouchableWithoutFeedback>

    {/* Dropdown */}
    <View
      testID="profileDropdownMenu"
      accessibilityRole="menu"
      accessibilityLabel={t('header.accessibility.profileOptionsMenu')}
      style={[
        styles.dropdownMenu,
        {
          top: dropdownPos.top,
          left: dropdownPos.left,
          right: dropdownPos.right,
          minWidth: dropdownPos.width,
        },
        zoomedMenuStyle,
      ]}
      {...(Platform.OS === 'web'
        ? {onKeyDown: (e: any) => handleDropdownMenuKeyDown(e, handleClose)}
        : {})}>
      <View style={styles.profileSubContainer}>
        {/* Row 1: Avatar + name/id/status */}
        <View style={[styles.profileSubChildContainer]}>
          {/* Avatar Circle */}
          <View style={styles.dropdownAvatar}>
            {image ? (
              <Image source={image} style={styles.imageContainer} resizeMode="cover" />
            ) : (
              <Text style={styles.text}>{name}</Text>
            )}
          </View>
          <View style={[styles.profileSubInnerContainer, isZoomed && {flexShrink: 1, minWidth: 0}]}>
            <TextTooltip
              tooltipText={fullName}
              disabled={fullName.length <= maxNameChars}
              placement="above"
              onVisibilityChange={setIsNameTooltipVisible}
              tooltipHorizontalAlign="right">
              <Text
                style={styles.dropdownUserName}
                testID="profileUserName"
                accessibilityRole="text"
                accessibilityLabel={`${t('header.accessibility.profileUserName')} ${displayFullName}`}>
                {displayFullName}
              </Text>
            </TextTooltip>
            <Text
              style={styles.dropdownUserId}
              testID="profileUserId"
              accessibilityRole="text"
              accessibilityLabel={`${t('header.accessibility.profileTechnicianId')} T-${selectedTechnician?.selectedTechnicianId}`}
              {...(Platform.OS === 'web' ? {tabIndex: 0} : {})}>
              {`T-${selectedTechnician?.selectedTechnicianId}`}
            </Text>
            <View
              style={styles.onLine}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`${t('dashboard.status')} ${shiftStarted ? t('header.online') : t('header.offline')}`}
              focusable={true}
              {...(Platform.OS === 'web' ? {tabIndex: 0} : {})}>
              <DotIcon color={shiftStarted ? theme.online : theme.offline} />
              <Text
                style={shiftStarted ? styles.onlineText : styles.offlineText}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants">
                {shiftStarted ? `${t('header.online')}` : `${t('header.offline')}`}
              </Text>
            </View>
          </View>
        </View>

        {/* Row 2: Theme colour picker */}
        {/* <View style={styles.separatorLine} />
        <View style={styles.themePickerContainer}>
          <View style={styles.themeSwatchRow}>
            {THEME_SWATCHES.map(swatch => (
              <TouchableOpacity
                key={swatch.id}
                testID={`themeSwatchButton-${swatch.id}`}
                accessible
                accessibilityRole="button"
                accessibilityLabel={`${swatch.label} theme`}
                accessibilityState={{selected: activeTheme === swatch.id}}
                onPress={() => dispatch(setClientTheme(swatch.id))}
                style={[
                  styles.themeSwatch,
                  {backgroundColor: swatch.color},
                  activeTheme === swatch.id && styles.themeSwatchActive,
                ]}
              />
            ))}
          </View>
        </View> */}
      </View>
      {/* <View style={styles.separatorLine} />
      <View style={styles.menuItem}>
        {profileOptions.map(item => {
          let IconComponent = item?.icon;
          return (
            <TouchableOpacity
              key={item.value.toString()}
              testID={`profileMenuItem-${item.value}`}
              accessible
              accessibilityRole="menuitem"
              accessibilityLabel={t(item.text)}
              accessibilityHint={t('header.accessibility.profileMenuItemHint')}
              style={[styles.menuSubItem]}
              onPress={() => handleSelect(item)}>
              <IconComponent color={theme.dropdownValuesIcon} />
              <Text style={[styles.menuItemText]}>{t(item.text)}</Text>
            </TouchableOpacity>
          );
        })}
      </View> */}
      <View style={styles.separatorLine} />
      <TouchableOpacity
        testID="profileLogoutButton"
        accessible
        accessibilityRole="button"
        accessibilityLabel={t('header.logOut')}
        accessibilityHint={t('header.accessibility.profileLogoutHint')}
        style={[styles.logOut]}
        onPress={() => logOut()}>
        <LogoutIcon color={theme.logout} />
        <Text style={[styles.logOutText]}>{t('header.logOut')}</Text>
      </TouchableOpacity>
    </View>
  </Modal>
  );
};

const ProfilePicture = ({
  name,
  image,
  online = false,
  containerStyle,
  fromHeader,
  onLogout,
  textColor,
  testID,
}: ProfileProps) => {
  const theme = useScreenTheme('profile');
  const styles = useMemo(() => createProfileStyles(theme), [theme]);
  const profileRef = React.useRef<View>(null);
  // Web: ref to the trigger button so focus can be restored when dropdown closes
  const buttonRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number | undefined;
    right: number | undefined;
    width: number;
  }>({
    top: 0,
    left: undefined,
    right: undefined,
    width: 0,
  });
  const {selectedTechnician} = useSelector(
    (state: RootState) => state.technician,
  );
  const {t} = useTranslation();
  const {shiftStarted} = useSelector(
    (state: RootState) => state.shiftManagement,
  );
  const {width} = useWindowDimensions();
  const [isNameTooltipVisible, setIsNameTooltipVisible] = useState(false);
  const fullName = selectedTechnician?.selectedTechnicianName ?? '';
  // Dynamic char limit based on viewport width; minimum 20 chars.
  const maxNameChars = Math.max(20, Math.floor(width / 60));
  const displayFullName =
    fullName && fullName.length > maxNameChars
      ? `${fullName.substring(0, maxNameChars)}...`
      : fullName;

  // Delegates to module-level measureAndSetDropdownPos to remove nested-function
  // CC from ProfilePicture (was the primary source of CC 28).
  const measureDropdown = () =>
    measureAndSetDropdownPos(profileRef as React.RefObject<View>, setDropdownPos);

  const handleOrientationChange = () => onOrientationChange(isOpen, measureDropdown);

  const handleSelect = (option: DropdownOption) => {
    setIsOpen(false);
  };

  /**
   * @function logout
   * @description Navigates the user to the agency selection screen for logging out.
   */
  const logOut = () => {
    setIsOpen(false);
    onLogout?.();
  };

  const handleToggleOpen = () => {
    measureDropdown();
    setIsOpen(prev => !prev);
  };

  const handleClose = () => closeDropdown(setIsOpen, buttonRef);

  if (!fromHeader) {
    return (
      <ProfileSimpleView
        name={name}
        image={image}
        online={online}
        textColor={textColor}
        containerStyle={containerStyle}
        styles={styles}
      />
    );
  }

  return (
    <>
      <TouchableOpacity
        testID={testID ?? 'headerProfileButton'}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={`${t('header.accessibility.profileMenuButton')} ${
          selectedTechnician?.selectedTechnicianName
        }`}
        accessibilityHint={t('header.accessibility.profileMenuHint')}
        accessibilityState={{expanded: isOpen}}
        style={[styles.profileContainer, containerStyle]}
        onPress={handleToggleOpen}
        focusable={true}
        {...(Platform.OS === 'web'
          ? {
              tabIndex: 0,
              onKeyDown: (e: any) => handleProfileTriggerKeyDown(e, handleToggleOpen),
            }
          : {})}
        ref={(node: any) => {
          (profileRef as any).current = node;
          buttonRef.current = node;
        }}>
        {/* Avatar Circle */}
        <View style={styles.avatar}>
          {image ? (
            <Image source={image} style={styles.imageContainer} resizeMode="cover" />
          ) : (
            <Text
              style={styles.text}
              testID="profileAvatarInitials"
              accessibilityLabel={`${t('header.accessibility.profileInitials')} ${name}`}>
              {name}
            </Text>
          )}
        </View>
        {/* Online Indicator */}
        {online && <View style={styles.onlineDot} />}
      </TouchableOpacity>
      {isOpen && (
        <ProfileDropdownMenu
          dropdownPos={dropdownPos}
          image={image}
          name={name}
          fullName={fullName}
          displayFullName={displayFullName}
          maxNameChars={maxNameChars}
          shiftStarted={shiftStarted}
          selectedTechnician={selectedTechnician}
          isNameTooltipVisible={isNameTooltipVisible}
          setIsNameTooltipVisible={setIsNameTooltipVisible}
          handleClose={handleClose}
          handleOrientationChange={handleOrientationChange}
          logOut={logOut}
          handleSelect={handleSelect}
          styles={styles}
          theme={theme}
          t={t}
        />
      )}
    </>
  );
};

export default ProfilePicture;
